import React, { useState, useEffect, useMemo, useRef } from 'react';
import DancingDotsProgress from '../components/ui/DancingDotsProgress';
import { IconList } from '../components/ui/Icons';
import { nativeDownload } from '../utils/nativeDownload';
import { API_URL } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const Profile = ({
    plants,
    userPlantData,
    fetchWithAuth,
    achievements,
    trackInteraction,
    messages,
    userProfile,
    user,
    categories,
    dailyStats,
    dailyChallenge, // Added prop
    logout,     // Added prop
    updateUser,  // Added prop
    selectedPlantList,
    setSelectedPlantList
}) => {
    const navigate = useNavigate();

    // Track PROFILE_VIEW beim Öffnen
    useEffect(() => {
        if (trackInteraction) {
            trackInteraction('PROFILE_VIEW', {});
        }
    }, [trackInteraction]);

    const [pwdMsg, setPwdMsg] = useState('');
    const [defaultPlantList, setDefaultPlantList] = useState(() => {
        return userProfile?.default_plant_list || 'GALABAUER';
    });

    const filteredPlants = useMemo(() => {
        if (!selectedPlantList) return plants;
        return plants.filter(p => p.plant_lists && p.plant_lists.includes(selectedPlantList));
    }, [plants, selectedPlantList]);

    const learned = Object.values(userPlantData).filter(u => u.learning_status === 'LEARNED' && filteredPlants.some(p => p.id === parseInt(u.plant_id))).length;
    // Note: userPlantData is keyed by plantId. We need to check if that plant belongs to current list.
    // Better way: iterate over filteredPlants and check status.

    // Recalculate stats based on filteredPlants
    const stats = useMemo(() => {
        let l = 0;
        let d = 0;
        filteredPlants.forEach(p => {
            const data = userPlantData[p.id];
            if (data?.learning_status === 'LEARNED') l++;
            if (data?.next_review_at && Date.now() >= new Date(data.next_review_at).getTime()) d++;
        });
        return { learned: l, total: filteredPlants.length, due: d };
    }, [filteredPlants, userPlantData]);

    // Aktualisiere defaultPlantList wenn userProfile sich ändert
    useEffect(() => {
        if (userProfile?.default_plant_list) {
            setDefaultPlantList(userProfile.default_plant_list);
        }
    }, [userProfile?.default_plant_list]);
    const [challengeExpanded, setChallengeExpanded] = useState(false);
    const [statsExpanded, setStatsExpanded] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [showChallengeDetail, setShowChallengeDetail] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const challengeContentRef = useRef(null);
    const statsContentRef = useRef(null);

    // Privatsphäre-Einstellungen State
    const [privacySettings, setPrivacySettings] = useState({
        is_public: false,
        show_avatar_publicly: false,
        show_stats_publicly: false,
        show_achievements_publicly: false,
        allow_sniggles_from_public: false,
        discoverable: false,
        bio: '',
        location: '',
        profile_slug: '',
        first_name: '',
        last_name: ''
    });
    const [privacyLoading, setPrivacyLoading] = useState(false);
    const [privacyMsg, setPrivacyMsg] = useState('');

    // Lade Avatar beim Mount
    useEffect(() => {
        const loadAvatar = async () => {
            if (user?.is_guest) return;
            try {
                const resp = await fetchWithAuth('/api/profile/');
                if (resp.ok) {
                    const data = await resp.json();
                    if (data.avatar_url) {
                        setAvatarUrl(data.avatar_url);
                    } else {
                        setAvatarUrl(null);
                    }
                    if (data.default_plant_list) {
                        setDefaultPlantList(data.default_plant_list);
                    }
                }
            } catch (err) {
                console.error('Fehler beim Laden des Avatars:', err);
            }
        };
        if (user && !user.is_guest) {
            loadAvatar();
        }
    }, [fetchWithAuth, user]);

    // Lade Privatsphäre-Einstellungen beim Mount
    useEffect(() => {
        const loadPrivacySettings = async () => {
            if (user?.is_guest) return;
            try {
                const resp = await fetchWithAuth('/api/profile/settings/privacy/');
                if (resp.ok) {
                    const data = await resp.json();
                    setPrivacySettings({
                        is_public: data.is_public || false,
                        show_avatar_publicly: data.show_avatar_publicly || false,
                        show_stats_publicly: data.show_stats_publicly || false,
                        show_achievements_publicly: data.show_achievements_publicly || false,
                        allow_sniggles_from_public: data.allow_sniggles_from_public || false,
                        discoverable: data.discoverable || false,
                        bio: data.bio || '',
                        location: data.location || '',
                        profile_slug: data.profile_slug || '',
                        first_name: data.first_name || '',
                        last_name: data.last_name || ''
                    });
                }
            } catch (err) {
                console.error('Fehler beim Laden der Privatsphäre-Einstellungen:', err);
            }
        };
        if (user && !user.is_guest) {
            loadPrivacySettings();
        }
    }, [fetchWithAuth, user]);

    // Speichere Privatsphäre-Einstellungen
    const savePrivacySettings = async (updates) => {
        setPrivacyLoading(true);
        setPrivacyMsg('');
        try {
            const newSettings = { ...privacySettings, ...updates };
            const resp = await fetchWithAuth('/api/profile/settings/privacy/', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newSettings)
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || data.detail || 'Fehler beim Speichern');
            setPrivacySettings(data);
            setPrivacyMsg('Einstellungen erfolgreich gespeichert.');
            setTimeout(() => setPrivacyMsg(''), 3000);
        } catch (err) {
            setPrivacyMsg(err.message || 'Fehler beim Speichern der Einstellungen.');
        } finally {
            setPrivacyLoading(false);
        }
    };

    // categoryProgress Berechnung
    const categoryProgress = useMemo(() => {
        if (!categories || !filteredPlants) return [];
        // Only include categories present in filteredPlants
        const activeCategoryIds = new Set(filteredPlants.map(p => p.category));
        const activeCategories = categories.filter(c => activeCategoryIds.has(c.id));

        const stats = activeCategories.map(cat => ({ id: cat.id, name: cat.name, total: 0, learned: 0 }));
        const idToStat = Object.fromEntries(stats.map(s => [s.id, s]));

        filteredPlants.forEach(p => {
            const s = idToStat[p.category];
            if (!s) return;
            s.total += 1;
            const status = userPlantData[p.id]?.learning_status || 'NOT_LEARNED';
            if (status === 'LEARNED') s.learned += 1;
        });
        return stats.filter(s => s.total > 0).sort((a, b) => a.name.localeCompare(b.name));
    }, [filteredPlants, categories, userPlantData]);

    // updateCollapsibleHeight Funktion
    const updateCollapsibleHeight = (ref, isExpanded) => {
        const content = ref.current;
        if (!content) return;
        requestAnimationFrame(() => {
            if (isExpanded) {
                const scrollHeight = content.scrollHeight;
                content.style.maxHeight = scrollHeight + 'px';
                content.style.opacity = '1';
                setTimeout(() => {
                    if (content.style.maxHeight !== '0px') {
                        content.style.maxHeight = 'none';
                    }
                }, 300);
            } else {
                const currentHeight = content.scrollHeight;
                content.style.maxHeight = currentHeight + 'px';
                content.offsetHeight;
                requestAnimationFrame(() => {
                    content.style.maxHeight = '0px';
                    content.style.opacity = '0';
                });
            }
        });
    };

    // useEffect für Challenge-Content
    useEffect(() => {
        if (dailyChallenge && challengeContentRef.current) {
            const timer = setTimeout(() => {
                updateCollapsibleHeight(challengeContentRef, challengeExpanded);
                if (challengeExpanded && trackInteraction) {
                    trackInteraction('CHALLENGE_VIEW', {
                        challenge_id: dailyChallenge.id,
                        challenge_type: dailyChallenge.challenge_type,
                        target_value: dailyChallenge.target_value
                    });
                }
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [challengeExpanded, dailyChallenge, trackInteraction]);

    // useEffect für Stats-Content
    useEffect(() => {
        if (dailyStats && statsContentRef.current) {
            const timer = setTimeout(() => {
                updateCollapsibleHeight(statsContentRef, statsExpanded);
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [statsExpanded, dailyStats]);

    // Initialisierung: Setze Collapsibles auf eingeklappt
    useEffect(() => {
        if (challengeContentRef.current) {
            challengeContentRef.current.style.maxHeight = '0px';
            challengeContentRef.current.style.opacity = '0';
        }
        if (statsContentRef.current) {
            statsContentRef.current.style.maxHeight = '0px';
            statsContentRef.current.style.opacity = '0';
        }
    }, []);

    const saveUsername = async (e) => {
        e.preventDefault(); setUsernameMsg('');
        try {
            const resp = await fetchWithAuth(`/api/profile/username/`, { method: 'POST', body: JSON.stringify({ username }) });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(data.detail || 'Fehler beim Speichern');
            setUsernameMsg('Benutzername aktualisiert.');
            if (updateUser) updateUser({ ...user, username: data.username || username });
        } catch (err) { setUsernameMsg(err.message); }
    };
    const saveEmail = async (e) => {
        e.preventDefault(); setEmailMsg('');
        try {
            const resp = await fetchWithAuth(`/api/profile/email/`, { method: 'POST', body: JSON.stringify({ email }) });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(data.detail || 'Fehler beim Speichern');
            setEmailMsg('E-Mail aktualisiert.');
        } catch (err) { setEmailMsg(err.message); }
    };
    const savePassword = async (e) => {
        e.preventDefault(); setPwdMsg('');
        try {
            const resp = await fetchWithAuth(`/api/profile/password/`, { method: 'POST', body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }) });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(data.detail || 'Fehler beim Speichern');
            setPwdMsg('Passwort aktualisiert.');
            setCurrentPassword(''); setNewPassword('');
        } catch (err) { setPwdMsg(err.message); }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setPwdMsg('Bitte wähle eine Bilddatei aus.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setPwdMsg('Die Datei ist zu groß (max. 5MB).');
            return;
        }

        const token = localStorage.getItem('authToken');
        if (!token) {
            setPwdMsg('Fehler: Nicht eingeloggt (Token fehlt).');
            return;
        }

        setAvatarLoading(true);
        setPwdMsg('Lade Profilbild hoch...');
        console.log('Starting avatar upload...', { fileName: file.name, fileSize: file.size });

        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const uploadUrl = `${API_URL}/api/profile/avatar/`;
            console.log('Upload URL:', uploadUrl);

            const resp = await fetch(uploadUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Content-type must NOT be set manually for FormData
                },
                body: formData
            });

            console.log('Upload response status:', resp.status);
            const data = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                console.error('Avatar upload failed:', data);
                if (resp.status === 413) {
                    throw new Error('Datei ist zu groß (Server-Limit).');
                }
                throw new Error(data.detail || `Upload fehlgeschlagen (${resp.status})`);
            }

            if (data.avatar_url) {
                setAvatarUrl(data.avatar_url);
                setPwdMsg('Profilbild erfolgreich hochgeladen.');
                // Force reload/update if needed, or rely on state
            } else {
                setPwdMsg('Profilbild hochgeladen, aber URL fehlt.');
            }
        } catch (err) {
            console.error('Avatar upload error:', err);
            setPwdMsg(`Fehler: ${err.message || 'Ein unbekannter Fehler ist aufgetreten.'}`);
        } finally {
            setAvatarLoading(false);
        }
    };

    const handleAvatarDelete = async () => {
        if (!confirm('Möchtest du dein Profilbild wirklich löschen?')) return;

        setAvatarLoading(true);
        setPwdMsg('');

        try {
            const resp = await fetchWithAuth(`/api/profile/avatar/`, { method: 'DELETE' });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(data.detail || 'Fehler beim Löschen');

            setAvatarUrl(null);
            setPwdMsg('Profilbild gelöscht.');
        } catch (err) {
            setPwdMsg(err.message);
        } finally {
            setAvatarLoading(false);
        }
    };

    const handleDeleteProfile = async () => {
        if (deleteConfirmText !== user?.username) {
            setPwdMsg('Bitte gib deinen Benutzernamen zur Bestätigung ein.');
            return;
        }

        setAvatarLoading(true);
        setPwdMsg('');

        try {
            const resp = await fetchWithAuth(`/api/profile/`, { method: 'DELETE' });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(data.detail || 'Fehler beim Löschen');

            alert('Dein Profil wurde erfolgreich gelöscht.');
            if (logout) logout();
            navigate('/login');
            window.location.reload();
        } catch (err) {
            setPwdMsg(err.message);
        } finally {
            setAvatarLoading(false);
            setShowDeleteConfirm(false);
            setDeleteConfirmText('');
        }
    };

    const requestCommunityAccess = async () => {
        if (!confirm('Möchtest du den Azubi+ Status beantragen, um Zugriff auf das Journal, die Pflanze der Woche und das Herbarium zu erhalten?')) return;
        try {
            const resp = await fetchWithAuth('/api/profile/request-community/', { method: 'POST' });
            const data = await resp.json();
            if (resp.ok) {
                showToast?.(data.message, 'success');
                // Reload profile data via props update if possible, or force reload local view
                window.location.reload(); 
            } else {
                showToast?.(data.error || 'Fehler beim Beantragen.', 'error');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const requestEmployerVerification = async () => {
        if (!confirm('Möchtest du die Verifizierung als Arbeitgeber beantragen?')) return;
        try {
            const resp = await fetchWithAuth('/api/profile/request-employer/', { method: 'POST' });
            const data = await resp.json();
            if (resp.ok) {
                showToast?.(data.message, 'success');
                window.location.reload();
            } else {
                showToast?.(data.error || 'Fehler beim Beantragen.', 'error');
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Remove local state for selectedPlantList, use props instead
    // const [selectedPlantList, setSelectedPlantList] = useState(() => localStorage.getItem('selectedPlantList') || 'GALABAUER');

    const [activeTab, setActiveTab] = useState('profile');
    const [myStats, setMyStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [statsTimeframe, setStatsTimeframe] = useState('recent_30d'); // 'recent_30d' or 'all_time'

    useEffect(() => {
        if (activeTab === 'stats' && !myStats) {
            setLoadingStats(true);
            fetchWithAuth(`${API_URL}/api/stats/mine/`)
                .then(r => r.json())
                .then(data => setMyStats(data))
                .catch(err => console.error(err))
                .finally(() => setLoadingStats(false));

            if (trackInteraction) {
                trackInteraction('STATS_VIEW', {});
            }
        }
    }, [activeTab, myStats, fetchWithAuth, trackInteraction]);

    const renderStats = () => {
        const handleDownloadPerformanceCert = async () => {
            setLoadingStats(true);
            try {
                const authToken = localStorage.getItem('authToken');
                await nativeDownload('/api/profile/certificate/performance/', authToken, (msg) => alert(msg));
            } finally {
                setLoadingStats(false);
            }
        };

        const handleDownloadAchievementCert = async (achId) => {
            setLoadingStats(true);
            try {
                const authToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                await nativeDownload(`/api/profile/certificate/achievement/${achId}/`, authToken, (msg) => alert(msg));
            } finally {
                setLoadingStats(false);
            }
        };

        if (loadingStats) return <div className="p-10 text-center"><div className="loader"></div>Lade ...</div>;
        return ((originalStats) => {
            const myStats = { ...originalStats };
            
            // Sicherstellen, dass alle verschachtelten Objekte existieren, um Abstürze zu vermeiden
            myStats.streaks = myStats.streaks || { current: 0, best: 0 };
            myStats.activity_14d = myStats.activity_14d || { labels: [], data_reviewed: [], data_correct: [] };
            myStats.activity_7d = myStats.activity_7d || { data: [0, 0, 0, 0, 0, 0, 0] };
            myStats.learning_progress = myStats.learning_progress || { learned: 0, total: 1, in_progress: 0, not_learned: 0 };
            if (!myStats.learning_progress.total || myStats.learning_progress.total === 0) myStats.learning_progress.total = 1; // Prevent division by zero
            myStats.duel_stats = myStats.duel_stats || { win_rate: 0, played: 0, won: 0, lost: 0 };
            myStats.category_stats = myStats.category_stats || [];
            myStats.quiz_performance = myStats.quiz_performance || { avg_accuracy: 0, total_cards_reviewed: 0, favorite_mode: 'Standard' };
            myStats.study_time = myStats.study_time || { this_week: 0 };
            myStats.achievements = myStats.achievements || { total: 0, earned: 0, percentage: 0 };

        // Access new data from enhanced backend
        const overview = myStats.overview || {};
        const studyTime = myStats.study_time || {};
        const quizPerf = myStats.quiz_performance || {};
        const achievements = myStats.achievements || {};
        const milestones = myStats.milestones || [];
        const trends = myStats.trends || {};

        return (
            <div className="space-y-6 animate-fadeIn font-inter">
                {/* Hero Section - Streamlined */}
                <div className="bg-gradient-to-br from-plantlix-500 to-plantlix-700 rounded-[32px] p-6 sm:p-8 text-white shadow-xl relative overflow-hidden ring-1 ring-white/10">
                    <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                        <svg className="w-48 h-48 transform rotate-12" fill="currentColor" viewBox="0 0 20 20"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" /><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" /></svg>
                    </div>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center relative z-10 gap-6">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            <div className="bg-white/10 p-5 rounded-2xl backdrop-blur-md border border-white/20 text-center min-w-[110px] shadow-inner">
                                <div className="text-[10px] uppercase tracking-[0.2em] font-black opacity-80 mb-1">Level</div>
                                <div className="text-6xl font-black leading-none drop-shadow-md">{overview.level || 1}</div>
                            </div>
                            <div className="space-y-2">
                                <div className="text-sm opacity-90 font-medium tracking-wide">Pflanze für Pflanze zur Meisterschaft</div>
                                <div className="flex items-end gap-3 hover:scale-[1.02] origin-left transition-transform">
                                    <div className="text-4xl font-black tracking-tight">{overview.total_xp || 0}</div>
                                    <div className="text-lg opacity-80 font-bold mb-1">XP Gesamt</div>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <div className="h-1.5 w-32 bg-black/20 rounded-full overflow-hidden">
                                        <div className="h-full bg-white rounded-full" style={{ width: `${overview.level_progress || 0}%` }}></div>
                                    </div>
                                    <div className="text-[10px] font-bold opacity-80 uppercase tracking-wider">
                                        {overview.next_level_xp || 0} XP to Lvl {(overview.level || 1) + 1}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="text-left md:text-right w-full md:w-auto flex flex-col items-start md:items-end gap-2 mt-4 md:mt-0">
                            <div className="flex flex-wrap md:flex-nowrap gap-2">
                                <div className="text-[9px] uppercase font-bold tracking-widest opacity-60 bg-black/5 px-3 py-1.5 rounded-lg border border-black/5 flex items-center gap-1.5 whitespace-nowrap">
                                    <span>📅 Mitglied seit:</span>
                                    <span className="text-black dark:text-white opacity-100">{myStats.usage_behavior?.member_since || 'Unbekannt'}</span>
                                </div>
                                <div className="text-[9px] uppercase font-bold tracking-widest opacity-60 bg-black/5 px-3 py-1.5 rounded-lg border border-black/5 flex items-center gap-1.5 whitespace-nowrap">
                                    <span>🕒 Letzter App-Start:</span>
                                    <span className="text-black dark:text-white opacity-100">{myStats.usage_behavior?.last_login || 'Unbekannt'}</span>
                                </div>
                            </div>
                            <button
                                onClick={handleDownloadPerformanceCert}
                                className="w-full md:w-auto text-sm bg-white text-plantlix-500 hover:bg-gray-50 active:scale-95 transition-all px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl font-bold flex items-center justify-center gap-2"
                            >
                                📜 Urkunde anzeigen
                            </button>
                        </div>
                    </div>
                </div>

                {/* DASHBOARD GRID */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* LEFT COLUMN: Core KPIs & Streaks */}
                    <div className="space-y-6">
                        
                        {/* 6 Key Metrics - Mini Grid */}
                        <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-[32px] border border-gray-100 dark:border-gray-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                            <h3 className="text-[10px] font-black text-gray-400 dark:text-gray-500 mb-5 uppercase tracking-[0.2em] px-1">Deine KPIs</h3>
                            <div className="grid grid-cols-2 gap-3 pb-1">
                                <div className="bg-gray-50 dark:bg-[#1e1e1e] p-4 rounded-2xl text-left hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700 flex flex-col justify-between h-[82px] items-start">
                                    <div className="text-2xl font-black text-plantlix-500 leading-none mb-1 w-full truncate text-left">{myStats.learning_progress.learned}</div>
                                    <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 text-left">Gelernt</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-[#1e1e1e] p-4 rounded-2xl text-left hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700 flex flex-col justify-between h-[82px] items-start">
                                    <div className="text-2xl font-black text-plantlix-500 leading-none mb-1 w-full truncate text-left">{quizPerf.avg_accuracy}%</div>
                                    <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 text-left">Präzision</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-[#1e1e1e] p-4 rounded-2xl text-left hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700 flex flex-col justify-between h-[82px] items-start">
                                    <div className="text-2xl font-black text-orange-500 leading-none mb-1 w-full truncate text-left">{myStats.streaks.current} 🔥</div>
                                    <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 text-left">Streak</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-[#1e1e1e] p-4 rounded-2xl text-left hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700 flex flex-col justify-between h-[82px] items-start">
                                    <div className="text-2xl font-black text-purple-500 leading-none mb-1 w-full truncate text-left">{studyTime.this_week}m</div>
                                    <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 text-left">Zeit (Wo.)</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-[#1e1e1e] p-4 rounded-2xl text-left hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700 flex flex-col justify-between h-[82px] items-start">
                                    <div className="text-2xl font-black text-yellow-500 leading-none mb-1 w-full truncate text-left">{myStats.duel_stats.won} ⚔️</div>
                                    <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 text-left">Siege</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-[#1e1e1e] p-4 rounded-2xl text-left hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-700 flex flex-col justify-between h-[82px] items-start">
                                    <div className="text-2xl font-black text-plantlix-500 leading-none mb-1 w-full truncate text-left">{achievements.earned} 🏆</div>
                                    <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 text-left">Erfolge</div>
                                </div>
                            </div>
                        </div>

                        {/* Progress Ring */}
                        <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-[32px] border border-gray-100 dark:border-gray-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden flex flex-col items-center">
                            <h3 className="text-[10px] font-black text-gray-400 dark:text-gray-500 mb-6 uppercase tracking-[0.2em] w-full text-left px-1">Enzyklopädie Fortschritt</h3>
                            <div className="relative w-36 h-36 mb-4 flex items-center justify-center">
                                <svg className="transform -rotate-90 w-32 h-32 sm:w-36 sm:h-36">
                                    <circle cx="72" cy="72" r="64" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-gray-100 dark:text-gray-800" />
                                    <circle cx="72" cy="72" r="64" stroke="currentColor" strokeWidth="12" fill="transparent"
                                        strokeDasharray={`${2 * Math.PI * 64}`}
                                        strokeDashoffset={`${2 * Math.PI * 64 * (1 - (myStats.learning_progress.learned / myStats.learning_progress.total))}`}
                                        className="text-plantlix-500 transition-all duration-1000" strokeLinecap="round" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <div className="text-3xl font-black text-gray-800 dark:text-white">
                                        {Math.round((myStats.learning_progress.learned / myStats.learning_progress.total) * 100)}%
                                    </div>
                                </div>
                            </div>
                            <div className="text-sm font-bold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-[#1e1e1e] px-5 py-3 rounded-xl w-full text-center border border-gray-100 dark:border-gray-800 shadow-sm mt-auto">
                                <span className="text-plantlix-500 font-black text-base">{myStats.learning_progress.learned}</span> von {myStats.learning_progress.total} gemeistert
                            </div>
                        </div>
                        
                        {/* Daily Insights */}
                        {trends.best_day && (
                            <div className="bg-gradient-to-br from-plantlix-50 to-emerald-50 dark:from-plantlix-900/20 dark:to-emerald-900/20 p-6 rounded-[32px] border border-plantlix-100 dark:border-plantlix-900 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                                <h3 className="text-[10px] font-black text-plantlix-400 dark:text-plantlix-500 mb-4 uppercase tracking-[0.2em]">🧠 AI Insight</h3>
                                <div className="space-y-4">
                                    <div className="bg-white/60 dark:bg-black/20 p-3 rounded-xl border border-white/50 dark:border-white/5">
                                        <div className="text-[10px] uppercase font-bold text-plantlix-500 dark:text-plantlix-400 mb-1 tracking-wider">Top Tag</div>
                                        <div className="text-sm text-plantlix-900 dark:text-plantlix-200">
                                            Am <span className="font-black text-plantlix-600 dark:text-plantlix-300">{trends.best_day}</span> lernst du am effektivsten
                                        </div>
                                    </div>
                                    
                                    {trends.learning_type && trends.learning_type !== 'Unbekannt' && (
                                        <div className="bg-white/60 dark:bg-black/20 p-3 rounded-xl border border-white/50 dark:border-white/5">
                                            <div className="text-[10px] uppercase font-bold text-plantlix-500 dark:text-plantlix-400 mb-1 tracking-wider">Dein Lerntyp</div>
                                            <div className="text-sm font-black text-plantlix-900 dark:text-plantlix-200">
                                                {trends.learning_type}
                                            </div>
                                        </div>
                                    )}

                                    {quizPerf.favorite_mode && quizPerf.favorite_mode !== 'Standard' && (
                                        <div className="bg-white/60 dark:bg-black/20 p-3 rounded-xl border border-white/50 dark:border-white/5">
                                            <div className="text-[10px] uppercase font-bold text-plantlix-500 dark:text-plantlix-400 mb-1 tracking-wider">Lieblingsmodus</div>
                                            <div className="text-sm font-black text-plantlix-900 dark:text-plantlix-200">
                                                {quizPerf.favorite_mode}-Quiz
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Difficult Plants (Hardest Plants) */}
                        {myStats.hardest_plants && myStats.hardest_plants.length > 0 && (
                            <div className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 p-6 rounded-[32px] border border-rose-100 dark:border-rose-900 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                                <h3 className="text-[10px] font-black text-rose-400 dark:text-rose-500 mb-4 uppercase tracking-[0.2em] flex items-center gap-1"><span>⚠️</span> Wiederholungsbedarf</h3>
                                <div className="space-y-3">
                                    {myStats.hardest_plants.map(hp => (
                                        <div key={hp.id} className="bg-white/60 dark:bg-black/20 p-3 rounded-xl border border-white/50 dark:border-white/5">
                                            <div className="text-[11px] font-black text-rose-900 dark:text-rose-200">{hp.name}</div>
                                            <div className="text-[10px] text-rose-700 dark:text-rose-400 mt-1 font-medium">
                                                {hp.incorrect}x falsch / {hp.correct}x richtig
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                    </div>

                    {/* MIDDLE & RIGHT: Deep Dives & Charts */}
                    <div className="lg:col-span-2 space-y-6 flex flex-col">
                        
                        {/* 14-Day Activity Chart */}
                        <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-[32px] border border-gray-100 dark:border-gray-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex-1 min-h-[300px] flex flex-col">
                            <h3 className="text-[10px] font-black text-gray-400 dark:text-gray-500 mb-6 uppercase tracking-[0.2em] px-1">Lernverlauf (Letzte 14 Tage)</h3>
                            <div className="flex-1 w-full min-h-[220px]">
                                {myStats.activity_14d && myStats.activity_14d.labels && myStats.activity_14d.labels.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart
                                            data={myStats.activity_14d.labels.map((label, i) => ({
                                                name: label,
                                                gespielt: myStats.activity_14d.data_reviewed[i] || 0,
                                                korrekt: myStats.activity_14d.data_correct[i] || 0
                                            }))}
                                            margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
                                        >
                                            <defs>
                                                <linearGradient id="colorGespielt" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#228B22" stopOpacity={0.2}/><stop offset="95%" stopColor="#228B22" stopOpacity={0}/></linearGradient>
                                                <linearGradient id="colorKorrekt" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/><stop offset="95%" stopColor="#10B981" stopOpacity={0}/></linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" strokeOpacity={0.5} />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af', fontWeight: 600 }} dy={10} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af', fontWeight: 600 }} />
                                            <Tooltip contentStyle={{ borderRadius: '16px', border: '1px solid #f3f4f6', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)', padding: '12px' }} itemStyle={{ fontWeight: '900' }} />
                                            <Area type="monotone" dataKey="gespielt" name="Gespielt" stroke="#228B22" strokeWidth={3} fillOpacity={1} fill="url(#colorGespielt)" />
                                            <Area type="monotone" dataKey="korrekt" name="Korrekt" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorKorrekt)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full flex items-center justify-center text-xs text-gray-400 font-medium">Noch keine Daten gesammelt</div>
                                )}
                            </div>
                        </div>

                        {/* Usage Behavior List & Categories */}
                        {myStats.usage_behavior && (
                            <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-[32px] border border-gray-100 dark:border-gray-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4 px-1">
                                    <h3 className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">Kategorien & Favoriten</h3>
                                    <div className="flex bg-gray-50 dark:bg-[#1e1e1e] p-1.5 rounded-[14px] border border-gray-100 dark:border-gray-700 shadow-inner">
                                        <button onClick={() => setStatsTimeframe('recent_30d')} className={`px-5 py-2 text-[11px] font-black tracking-wide uppercase rounded-xl transition-all ${statsTimeframe === 'recent_30d' ? 'bg-plantlix-500 text-white shadow-md' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>30 Tage</button>
                                        <button onClick={() => setStatsTimeframe('all_time')} className={`px-5 py-2 text-[11px] font-black tracking-wide uppercase rounded-xl transition-all ${statsTimeframe === 'all_time' ? 'bg-plantlix-500 text-white shadow-md' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>Gesamt</button>
                                    </div>
                                </div>
                                
                                <div className="grid sm:grid-cols-2 gap-8 px-1">
                                    {/* Top Plants */}
                                    <div>
                                        <h4 className="text-[10px] uppercase text-gray-400 font-bold mb-4 tracking-wider">Häufigste Pflanzen</h4>
                                        <div className="space-y-3">
                                            {(myStats.usage_behavior[statsTimeframe]?.top_plants || []).slice(0, 4).map((p, i) => (
                                                <div key={p.id} className="flex items-center justify-between group py-2.5 border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-gray-50/50 dark:hover:bg-white/5 rounded-lg px-2 -mx-2 transition-colors cursor-default overflow-hidden">
                                                    <div className="flex items-center gap-4 min-w-0 flex-1 overflow-hidden">
                                                        <span className="text-sm font-black text-gray-200 dark:text-gray-700 group-hover:text-plantlix-500 transition-colors w-4 flex-shrink-0 text-center">{i + 1}</span>
                                                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate pr-2 block whitespace-nowrap overflow-hidden text-ellipsis" title={p.name}>{p.name}</span>
                                                    </div>
                                                    <span className="text-[10px] font-black text-plantlix-500 bg-plantlix-50 dark:bg-plantlix-900/20 px-2.5 py-1 rounded-md flex-shrink-0 ml-2">{p.count}x</span>
                                                </div>
                                            ))}
                                            {(myStats.usage_behavior[statsTimeframe]?.top_plants || []).length === 0 && (
                                                <div className="text-xs text-gray-400">Keine Daten verfügbar</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Categories & Modes */}
                                    <div className="space-y-8">
                                        <div>
                                            <h4 className="text-[10px] uppercase text-gray-400 font-bold mb-4 tracking-wider">Top Lektionen</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {(myStats.usage_behavior[statsTimeframe]?.top_categories || []).slice(0, 5).map(c => (
                                                    <span key={c.id} className="px-3 py-1.5 bg-plantlix-50/80 dark:bg-plantlix-900/20 border border-plantlix-100 dark:border-plantlix-800/50 text-plantlix-500 text-[11px] font-black rounded-xl capitalize">
                                                        {c.name} <span className="opacity-60 ml-1">({c.count})</span>
                                                    </span>
                                                ))}
                                                {(myStats.usage_behavior[statsTimeframe]?.top_categories || []).length === 0 && (
                                                    <div className="text-xs text-gray-400">Keine Daten verfügbar</div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-[10px] uppercase text-gray-400 font-bold mb-3 tracking-wider">Quiz Nutzung</h4>
                                            {(myStats.usage_behavior[statsTimeframe]?.top_quizzes || []).map(q => (
                                                <div key={q.mode} className="flex justify-between items-center text-sm py-2 text-gray-600 dark:text-gray-400 border-b border-gray-50 dark:border-gray-800/50 last:border-0">
                                                    <span className="font-semibold capitalize">{q.mode}</span>
                                                    <span className="font-black text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-lg text-xs">{q.count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Achievements Row */}
                        {achievements.total > 0 && (
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-6 rounded-[32px] border border-amber-100 dark:border-amber-800/50 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
                                <div className="flex-1 w-full">
                                    <div className="flex justify-between items-end mb-2">
                                        <h3 className="text-sm font-black text-amber-900 dark:text-amber-400 flex items-center gap-2">
                                            <span className="text-xl">🏆</span> {achievements.earned} / {achievements.total} Erfolge
                                        </h3>
                                        <span className="text-xs font-bold text-amber-600 dark:text-amber-500 bg-white/50 dark:bg-black/20 px-2 py-0.5 rounded-md">{achievements.percentage}%</span>
                                    </div>
                                    <div className="h-3 bg-white/60 dark:bg-black/30 rounded-full w-full overflow-hidden shadow-inner">
                                         <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full" style={{ width: `${achievements.percentage}%` }}></div>
                                    </div>
                                </div>
                                <button onClick={() => navigate('/badges')} className="w-full sm:w-auto text-xs bg-white text-amber-700 shadow-sm border border-amber-200 px-6 py-3 rounded-xl font-bold whitespace-nowrap hover:bg-amber-100 transition-colors active:scale-95">
                                    Urkunden ansehen
                                </button>
                            </div>
                        )}
                        
                    </div>
                </div>
            </div>
        );

        })(myStats || {});
    };

    return (
        <div className={`p-4 sm:p-6 md:p-10 mx-auto transition-all duration-500 ${activeTab === 'stats' ? 'max-w-6xl' : 'max-w-3xl'}`}>
            {/* Header / Tabs */}
            <div className="mb-0">
                <div className="bg-white dark:bg-[#1e1e1e] rounded-t-3xl rounded-b-none shadow-xl shadow-gray-200/50 dark:shadow-none p-8 border border-gray-100 dark:border-gray-800 border-b-0 pb-0">
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Dein Profil</h1>
                        {/* Unread Snipple Badge */}
                        {messages && messages.unread_count > 0 && (
                            <span className="px-3 py-1 rounded-full bg-plantlix-500 text-white text-sm font-bold animate-pulse">
                                {messages.unread_count} Sniggle{messages.unread_count !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => setActiveTab('profile')}
                            className={`pb-3 px-1 font-semibold text-lg transition-colors relative ${activeTab === 'profile' ? 'text-plantlix-500 dark:text-plantlix-400' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                        >
                            Einstellungen
                            {activeTab === 'profile' && <div className="absolute bottom-0 left-0 w-full h-1 bg-plantlix-500 rounded-t-full"></div>}
                        </button>
                        <button
                            onClick={() => setActiveTab('stats')}
                            className={`pb-3 px-1 font-semibold text-lg transition-colors relative ${activeTab === 'stats' ? 'text-plantlix-500 dark:text-plantlix-400' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                        >
                            Auswertung 📊
                            {activeTab === 'stats' && <div className="absolute bottom-0 left-0 w-full h-1 bg-plantlix-500 rounded-t-full"></div>}
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="bg-white dark:bg-[#1e1e1e] rounded-b-3xl shadow-xl shadow-gray-200/50 dark:shadow-none p-8 border border-gray-100 dark:border-gray-800 border-t-0 min-h-[500px]">

                {activeTab === 'stats' ? renderStats() : (
                    <div className="animate-fadeIn">
                        {/* Old Profile Content Wrapper */}

                        {/* Profilbild Logic */}
                        {!user?.is_guest && (
                            <div className="mb-6 p-4 bg-gray-50 dark:bg-[#1e1e1e] rounded-lg border border-gray-200 dark:border-gray-800">
                                <h2 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">Profilbild</h2>
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        {avatarUrl ? (
                                            <img
                                                src={avatarUrl}
                                                alt="Profilbild"
                                                className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 dark:border-gray-700"
                                                onError={(e) => {
                                                    setAvatarUrl(null);
                                                }}
                                            />
                                        ) : (
                                            <div className="w-24 h-24 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-4xl text-gray-500 dark:text-gray-400 border-2 border-gray-300 dark:border-gray-700">
                                                👤
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2 flex-1">
                                        <label className="cursor-pointer">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleAvatarUpload}
                                                className="hidden"
                                                disabled={avatarLoading}
                                                id="avatar-upload-input"
                                            />
                                            <span className="inline-block px-4 py-2 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                                {avatarLoading ? 'Wird hochgeladen...' : avatarUrl ? 'Bild ändern' : 'Bild hochladen'}
                                            </span>
                                        </label>
                                        {avatarUrl && (
                                            <button
                                                onClick={handleAvatarDelete}
                                                disabled={avatarLoading}
                                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Bild löschen
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {pwdMsg && pwdMsg.includes('Profilbild') && (
                                    <div className={`text-sm mt-2 ${pwdMsg.includes('erfolgreich') || pwdMsg.includes('gelöscht') ? 'text-plantlix-600 dark:text-plantlix-400' : 'text-red-600 dark:text-red-400'}`}>
                                        {pwdMsg}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Persönliche Daten */}
                        {!user?.is_guest && (
                            <div className="mb-6 p-4 bg-gray-50 dark:bg-[#1e1e1e] rounded-lg border border-gray-200 dark:border-gray-800">
                                <h2 className="font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <span>👤</span>
                                    <span>Persönliche Daten</span>
                                </h2>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-4 bg-plantlix-50 dark:bg-plantlix-900/10 p-2 rounded border border-plantlix-100 dark:border-plantlix-900/30">
                                    Diese Angaben sind optional, werden aber für offizielle Dokumente (Urkunden, Ausbildungsnachweis) verwendet.
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Vorname</label>
                                        <input
                                            type="text"
                                            value={privacySettings.first_name}
                                            onChange={(e) => setPrivacySettings({ ...privacySettings, first_name: e.target.value })}
                                            onBlur={() => savePrivacySettings({ first_name: privacySettings.first_name })}
                                            placeholder="z.B. Max"
                                            className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-plantlix-500 outline-none transition"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nachname</label>
                                        <input
                                            type="text"
                                            value={privacySettings.last_name}
                                            onChange={(e) => setPrivacySettings({ ...privacySettings, last_name: e.target.value })}
                                            onBlur={() => savePrivacySettings({ last_name: privacySettings.last_name })}
                                            placeholder="z.B. Mustermann"
                                            className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-plantlix-500 outline-none transition"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Privatsphäre-Einstellungen */}
                        {!user?.is_guest && (
                            <div className="mb-6 p-4 bg-gray-50 dark:bg-[#1e1e1e] rounded-lg border border-gray-200 dark:border-gray-800">
                                <h2 className="font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <span>🔒</span>
                                    <span>Privatsphäre & Öffentliches Profil</span>
                                </h2>

                                <div className="space-y-4">
                                    {privacySettings.is_public && (
                                        <div className="p-4 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-lg border-2 border-plantlix-300 dark:border-plantlix-700">
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
                                                        <span>🌐</span>
                                                        <span>Dein Profil ist öffentlich!</span>
                                                    </div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        {privacySettings.profile_slug ? `/profile/@${privacySettings.profile_slug}` : 'Profil-Slug wird generiert...'}
                                                    </div>
                                                </div>
                                                {privacySettings.profile_slug && (
                                                    <button
                                                        onClick={() => {
                                                            navigate(`/profile/@${privacySettings.profile_slug}`);
                                                        }}
                                                        className="px-5 py-2.5 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
                                                    >
                                                        <span>Profil ansehen</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <PrivacyToggle 
                                            label="Öffentliches Profil" 
                                            description="Andere können dein Profil und deine Erfolge sehen."
                                            checked={privacySettings.is_public}
                                            onChange={(val) => savePrivacySettings({ is_public: val })}
                                            loading={privacyLoading}
                                        />
                                        <PrivacyToggle 
                                            label="In der Suche auffindbar" 
                                            description="Andere Nutzer können dich über die Suche finden."
                                            checked={privacySettings.discoverable}
                                            onChange={(val) => savePrivacySettings({ discoverable: val })}
                                            loading={privacyLoading}
                                        />
                                        <PrivacyToggle 
                                            label="Statistiken teilen" 
                                            description="XP, Streak und Fortschritt öffentlich machen."
                                            checked={privacySettings.show_stats_publicly}
                                            onChange={(val) => savePrivacySettings({ show_stats_publicly: val })}
                                            loading={privacyLoading}
                                        />
                                        <PrivacyToggle 
                                            label="Öffentliche Sniggles" 
                                            description="Erlaube Nachrichten von Nutzern außerhalb deiner Freunde."
                                            checked={privacySettings.allow_sniggles_from_public}
                                            onChange={(val) => savePrivacySettings({ allow_sniggles_from_public: val })}
                                            loading={privacyLoading}
                                        />
                                    </div>

                                    <div className="space-y-6 pt-8 border-t border-gray-100 dark:border-white/5">
                                        <div>
                                            <label className="block text-sm font-black text-gray-400 uppercase tracking-widest mb-3">Über mich (Bio)</label>
                                            <textarea
                                                value={privacySettings.bio}
                                                placeholder="Erzähle etwas über dich oder deine Pflanzen..."
                                                onChange={(e) => setPrivacySettings({ ...privacySettings, bio: e.target.value })}
                                                onBlur={() => savePrivacySettings({ bio: privacySettings.bio })}
                                                maxLength={500}
                                                rows={4}
                                                className="w-full p-6 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-3xl focus:ring-4 focus:ring-plantlix-500/10 focus:border-plantlix-500 transition-all text-gray-900 dark:text-white font-medium placeholder-gray-400 resize-none"
                                            />
                                            <div className="flex justify-end mt-2">
                                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{privacySettings.bio.length} / 500</span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-black text-gray-400 uppercase tracking-widest mb-3">Wohnort / Region</label>
                                                <input
                                                    type="text"
                                                    value={privacySettings.location}
                                                    placeholder="z.B. Dresden, Sachsen"
                                                    onChange={(e) => setPrivacySettings({ ...privacySettings, location: e.target.value })}
                                                    onBlur={() => savePrivacySettings({ location: privacySettings.location })}
                                                    className="w-full px-6 py-4 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-2xl focus:ring-4 focus:ring-plantlix-500/10 focus:border-plantlix-500 transition-all text-gray-900 dark:text-white font-medium"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-black text-gray-400 uppercase tracking-widest mb-3">Profil-URL (Slug)</label>
                                                <div className="relative group">
                                                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 font-bold select-none text-sm group-focus-within:text-plantlix-500 transition-colors">@</span>
                                                    <input
                                                        type="text"
                                                        value={privacySettings.profile_slug}
                                                        placeholder="dein_name"
                                                        onChange={(e) => setPrivacySettings({ ...privacySettings, profile_slug: e.target.value })}
                                                        onBlur={() => savePrivacySettings({ profile_slug: privacySettings.profile_slug })}
                                                        className="w-full pl-10 pr-6 py-4 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-2xl focus:ring-4 focus:ring-plantlix-500/10 focus:border-plantlix-500 transition-all text-gray-900 dark:text-white font-black"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {privacyMsg && (
                                        <div className={`p-4 rounded-2xl text-sm font-bold animate-in fade-in slide-in-from-top-2 duration-300 ${privacyMsg.includes('Fehler') ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-plantlix-50 text-plantlix-700 border border-plantlix-100'}`}>
                                            {privacyMsg}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            setPwdMsg('');
                            try {
                                const resp = await fetchWithAuth(`/api/profile/default-plant-list/`, {
                                    method: 'POST',
                                    body: JSON.stringify({ default_plant_list: defaultPlantList })
                                });
                                const data = await resp.json().catch(() => ({}));
                                if (!resp.ok) throw new Error(data.detail || 'Fehler beim Speichern');
                                setPwdMsg('Standard-Pflanzenliste aktualisiert.');
                            } catch (err) {
                                setPwdMsg(err.message);
                            }
                        }} className="bg-gray-50 dark:bg-[#1e1e1e] p-4 rounded-lg border border-gray-200 dark:border-gray-800 mt-6">
                            <h2 className="flex items-center gap-2 font-semibold mb-3 text-gray-800 dark:text-gray-100">
                                <IconList className="text-plantlix-500 dark:text-plantlix-400" />
                                Standard-Pflanzenliste
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Diese Liste wird beim App-Start automatisch geladen. Du kannst sie jederzeit temporär in den Filtern umschalten.</p>
                            <select
                                value={defaultPlantList}
                                onChange={e => setDefaultPlantList(e.target.value)}
                                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-2 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-plantlix-500 focus:border-plantlix-500 transition-all"
                            >
                                <option value="GALABAUER">GaLaBau Sachsen</option>
                                <option value="FRIEDHOFSGAERTNER">Friedhof Sachsen</option>
                                <option value="ZIERPFLANZENGAERTNER">Zierpflanzen Sachsen</option>
                            </select>
                            <button type="submit" className="px-6 py-3 min-h-[48px] bg-plantlix-500 hover:bg-plantlix-600 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-none active:bg-plantlix-700">
                                💾 Speichern
                            </button>
                            {pwdMsg && (pwdMsg.includes('Pflanzenliste') || pwdMsg.includes('Standard')) && <div className={`text-sm mt-2 ${pwdMsg.includes('aktualisiert') ? 'text-plantlix-600 dark:text-plantlix-400' : 'text-red-600 dark:text-red-400'}`}>{pwdMsg}</div>}
                        </form>


                        {/* Profil löschen Button */}
                        {!user?.is_guest && (
                            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-200 dark:border-red-800">
                                {!showDeleteConfirm ? (
                                    <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">Profil löschen</button>
                                ) : (
                                    <div className="space-y-3">
                                        <p className="text-sm font-semibold text-red-800 dark:text-red-200">Gib deinen Benutzernamen ein: <strong>{user?.username}</strong></p>
                                        <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} className="w-full p-3 border-2 border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-[#1a1a1a]" />
                                        <div className="flex gap-2">
                                            <button onClick={handleDeleteProfile} disabled={avatarLoading || deleteConfirmText !== user?.username} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">Endgültig löschen</button>
                                            <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg text-sm font-medium">Abbrechen</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Challenge Detail Modal */}
            {showChallengeDetail && dailyChallenge && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fadeIn" onClick={() => setShowChallengeDetail(false)}>
                    <div className="card rounded-card max-w-2xl w-full p-8" onClick={e => e.stopPropagation()}>
                        {/* ... Challenge Detail Content ... */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowChallengeDetail(false);
                                    navigate('/quiz');
                                }}
                                className="flex-1 px-6 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg shadow-lg"
                            >
                                {dailyChallenge.completed ? '🎉 Neue Challenge starten' : '🚀 Challenge starten'}
                            </button>
                            <button onClick={() => setShowChallengeDetail(false)} className="px-6 py-4 bg-white/20 backdrop-blur-sm text-white rounded-xl">Schließen</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;

const PrivacyToggle = ({ label, description, checked, onChange, loading }) => (
    <div className="flex items-center justify-between p-6 bg-gray-50 dark:bg-white/[0.03] rounded-3xl border border-gray-100 dark:border-white/5 hover:border-plantlix-500/30 transition-all group">
        <div className="flex-1 pr-4">
            <div className="font-black text-gray-900 dark:text-white mb-1 group-hover:text-plantlix-600 dark:group-hover:text-plantlix-400 transition-colors">{label}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium leading-relaxed">{description}</div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="sr-only peer"
                disabled={loading}
            />
            <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-800 peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-plantlix-500 shadow-inner"></div>
        </label>
    </div>
);
